import React, { useState, useEffect, useCallback } from "react";
import {
  Page,
  Layout,
  Card,
  DataTable,
  Badge,
  Spinner,
  Banner,
  EmptyState,
  Text,
  Box,
  Button,
  ButtonGroup,
  TextField,
  Modal,
  TextContainer,
  Filters,
  ChoiceList,
  Pagination,
  LegacyStack,
} from "@shopify/polaris";
import {
  DeleteIcon,
  ExportIcon,
  SearchIcon,
  PersonIcon,
} from "@shopify/polaris-icons";
import { useShop } from "../context/ShopContext.jsx";
import { useI18n } from "../context/I18nContext.jsx";

export default function CustomersPage() {
  const { apiFetch } = useShop();
  const { t } = useI18n();
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [marketingFilter, setMarketingFilter] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [deleteModal, setDeleteModal] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [detailModal, setDetailModal] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState(null);

  const loadCustomers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: String(page), limit: "25" });
      if (search) params.set("search", search);
      if (marketingFilter === "true") params.set("marketing", "true");
      const data = await apiFetch(`/customers?${params}`);
      setCustomers(data.customers || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [page, search, marketingFilter, apiFetch]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  const handleSearch = () => {
    setPage(1);
    setSearch(searchValue);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  const handleClearSearch = () => {
    setSearchValue("");
    setSearch("");
    setPage(1);
  };

  const handleExport = async (marketingOnly) => {
    setExporting(true);
    try {
      const res = await fetch("/api/customers/export", {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-shop-domain": localStorage.getItem("zyncfit_shop") || "" },
        body: JSON.stringify({ marketingOnly }),
      });
      if (!res.ok) throw new Error(t("customers.exportFailed"));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "zyncfit-klanten.csv";
      a.click();
      URL.revokeObjectURL(url);
      setSuccessMsg(marketingOnly ? t("customers.marketingExported") : t("customers.allExported"));
    } catch (err) {
      setError(err.message);
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal) return;
    setDeleting(true);
    try {
      await apiFetch(`/customers/${deleteModal.id}`, { method: "DELETE" });
      setDeleteModal(null);
      setSuccessMsg(t("customers.deleted"));
      loadCustomers();
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleMarketing = async (customerId) => {
    try {
      await apiFetch(`/customers/${customerId}/toggle-marketing`, { method: "POST" });
      loadCustomers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleViewDetail = async (customerId) => {
    setDetailModal(customerId);
    setDetailLoading(true);
    try {
      const data = await apiFetch(`/customers/${customerId}`);
      setDetailData(data.customer);
    } catch (err) {
      setError(err.message);
      setDetailModal(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const rows = customers.map((c) => [
    <Button variant="plain" onClick={() => handleViewDetail(c.id)}>
      {c.email}
    </Button>,
    c.name || "—",
    c.sessionCount || 0,
    <Badge tone={c.marketingConsent ? "success" : "warning"}>
      {c.marketingConsent ? t("customers.optIn") : t("customers.noConsent")}
    </Badge>,
    <Badge tone={c.privacyAccepted ? "success" : "attention"}>
      {c.privacyAccepted ? t("customers.accepted") : t("customers.notAccepted")}
    </Badge>,
    new Date(c.createdAt).toLocaleDateString("nl-NL"),
    <ButtonGroup>
      <Button
        size="slim"
        onClick={() => handleToggleMarketing(c.id)}
      >
        {c.marketingConsent ? t("customers.marketingOff") : t("customers.marketingOn")}
      </Button>
      <Button
        size="slim"
        tone="critical"
        icon={DeleteIcon}
        onClick={() => setDeleteModal(c)}
      />
    </ButtonGroup>,
  ]);

  return (
    <Page
      title={t("customers.title")}
      subtitle={`${total} klant${total !== 1 ? "en" : ""} geregistreerd`}
      primaryAction={{
        content: t("customers.exportMarketing"),
        icon: ExportIcon,
        onAction: () => handleExport(true),
        loading: exporting,
      }}
      secondaryActions={[
        {
          content: t("customers.exportAll"),
          icon: ExportIcon,
          onAction: () => handleExport(false),
          loading: exporting,
        },
      ]}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical" onDismiss={() => setError(null)}>
              {error}
            </Banner>
          </Layout.Section>
        )}
        {successMsg && (
          <Layout.Section>
            <Banner tone="success" onDismiss={() => setSuccessMsg(null)}>
              {successMsg}
            </Banner>
          </Layout.Section>
        )}

        {/* Privacy notice */}
        <Layout.Section>
          <Banner tone="info">
            <Text variant="bodyMd">
              {t("customers.gdprBanner")}
            </Text>
          </Banner>
        </Layout.Section>

        {/* Search & filters */}
        <Layout.Section>
          <Card>
            <Box padding="400">
              <LegacyStack distribution="fill" spacing="tight">
                <LegacyStack.Item fill>
                  <TextField
                    label=""
                    labelHidden
                    placeholder={t("customers.searchPlaceholder")}
                    value={searchValue}
                    onChange={setSearchValue}
                    onKeyDown={handleSearchKeyDown} 
                    autoComplete="off"
                    connectedRight={
                      <Button onClick={handleSearch} icon={SearchIcon}>
                        {t("common.search")}
                      </Button>
                    }
                    clearButton
                    onClearButtonClick={handleClearSearch}
                  />
                </LegacyStack.Item>
                <LegacyStack.Item>
                  <ButtonGroup segmented>
                    <Button
                      pressed={marketingFilter === null}
                      onClick={() => { setMarketingFilter(null); setPage(1); }}
                    >
                      {t("customers.filterAll")}
                    </Button>
                    <Button
                      pressed={marketingFilter === "true"}
                      onClick={() => { setMarketingFilter("true"); setPage(1); }}
                    >
                      {t("customers.filterOptIn")}
                    </Button>
                  </ButtonGroup>
                </LegacyStack.Item>
              </LegacyStack>
            </Box>
          </Card>
        </Layout.Section>

        {/* Customer table */}
        <Layout.Section>
          <Card>
            {loading ? (
              <Box padding="400">
                <Spinner />
              </Box>
            ) : customers.length === 0 ? (
              <EmptyState
                heading={t("customers.noCustomers")}
                image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
              >
                <p>{t("customers.noCustomersDesc")}</p>
              </EmptyState>
            ) : (
              <>
                <DataTable
                  columnContentTypes={["text", "text", "numeric", "text", "text", "text", "text"]}
                  headings={[t("common.email"), t("common.name"), t("customers.sessionsCol"), t("customers.marketingCol"), t("customers.privacyCol"), t("customers.registeredCol"), t("common.actions")]}
                  rows={rows}
                />
                <Box padding="400">
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    <Pagination
                      hasPrevious={page > 1}
                      hasNext={page < totalPages}
                      onPrevious={() => setPage((p) => Math.max(1, p - 1))}
                      onNext={() => setPage((p) => p + 1)}
                    />
                  </div>
                </Box>
              </>
            )}
          </Card>
        </Layout.Section>

        {/* Stats cards */}
        <Layout.Section variant="oneThird">
          <Card>
            <Box padding="400">
              <Text variant="headingSm">{t("customers.totalCustomers")}</Text>
              <Text variant="heading2xl">{total}</Text>
            </Box>
          </Card>
        </Layout.Section>
        <Layout.Section variant="oneThird">
          <Card>
            <Box padding="400">
              <Text variant="headingSm">{t("customers.marketingOptIn")}</Text>
              <Text variant="heading2xl">
                {customers.filter((c) => c.marketingConsent).length}
                <Text as="span" variant="bodyMd" tone="subdued"> / {customers.length}</Text>
              </Text>
            </Box>
          </Card>
        </Layout.Section>
      </Layout>

      {/* Delete confirmation modal */}
      {deleteModal && (
        <Modal
          open
          onClose={() => setDeleteModal(null)}
          title={t("customers.deleteTitle")}
          primaryAction={{
            content: t("customers.deleteConfirm"),
            destructive: true,
            loading: deleting,
            onAction: handleDelete,
          }}
          secondaryActions={[{ content: t("common.cancel"), onAction: () => setDeleteModal(null) }]}
        >
          <Modal.Section>
            <TextContainer>
              <p>
                {t("customers.deleteWarning")}
              </p>
            </TextContainer>
          </Modal.Section>
        </Modal>
      )}

      {/* Customer detail modal */}
      {detailModal && (
        <Modal
          open
          onClose={() => { setDetailModal(null); setDetailData(null); }}
          title={t("customers.detailsTitle")}
          large
        >
          <Modal.Section>
            {detailLoading ? (
              <Spinner />
            ) : detailData ? (
              <TextContainer>
                <LegacyStack vertical spacing="tight">
                  <Text variant="headingMd">{detailData.name || t("customers.noName")}</Text>
                  <Text variant="bodyMd"><strong>E-mail:</strong> {detailData.email}</Text>
                  <Text variant="bodyMd"><strong>Telefoon:</strong> {detailData.phone || "—"}</Text>
                  <Text variant="bodyMd">
                    <strong>Marketing toestemming:</strong>{" "}
                    <Badge tone={detailData.marketingConsent ? "success" : "warning"}>
                      {detailData.marketingConsent ? "Ja (opt-in)" : "Nee"}
                    </Badge>
                  </Text>
                  {detailData.consentDate && (
                    <Text variant="bodyMd">
                      <strong>Toestemming op:</strong>{" "}
                      {new Date(detailData.consentDate).toLocaleString("nl-NL")}
                    </Text>
                  )}
                  {detailData.consentIp && (
                    <Text variant="bodyMd">
                      <strong>Consent IP:</strong> {detailData.consentIp}
                    </Text>
                  )}
                  <Text variant="bodyMd">
                    <strong>Privacy geaccepteerd:</strong>{" "}
                    <Badge tone={detailData.privacyAccepted ? "success" : "attention"}>
                      {detailData.privacyAccepted ? "Ja" : "Nee"}
                    </Badge>
                  </Text>
                  <Text variant="bodyMd">
                    <strong>Geregistreerd:</strong>{" "}
                    {new Date(detailData.createdAt).toLocaleString("nl-NL")}
                  </Text>
                  <Text variant="bodyMd">
                    <strong>Shopify klant-ID:</strong>{" "}
                    {detailData.shopifyCustomerId || t("customers.notSynced")}
                  </Text>
                  <Text variant="headingSm" as="h3">
                    Paskamer sessies ({detailData.sessions?.length || 0})
                  </Text>
                  {detailData.sessions?.length > 0 ? (
                    <DataTable
                      columnContentTypes={["text", "text", "text"]}
                      headings={["Product", "Datum", "E-mail verzonden"]}
                      rows={detailData.sessions.map((sess) => [
                        sess.productId,
                        new Date(sess.createdAt).toLocaleDateString("nl-NL"),
                        sess.emailSent ? "Ja" : "Nee",
                      ])}
                    />
                  ) : (
                    <Text variant="bodyMd" tone="subdued">Nog geen sessies.</Text>
                  )}
                </LegacyStack>
              </TextContainer>
            ) : null}
          </Modal.Section>
        </Modal>
      )}
    </Page>
  );
}
